import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth } from '../config/firebase';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // List of admin email addresses (you can move this to environment variables or a database)
  const ADMIN_EMAILS = process.env.REACT_APP_ADMIN_EMAILS 
    ? process.env.REACT_APP_ADMIN_EMAILS.split(',')
    : [];
  
  // Super admin email (the main admin)
  const SUPER_ADMIN_EMAIL = process.env.REACT_APP_SUPER_ADMIN_EMAIL || '';

  // Check authentication state on mount and when auth changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Check if user email is in admin list
        const email = user.email;
        const isAdmin = ADMIN_EMAILS.length === 0 || ADMIN_EMAILS.includes(email);
        const isSuper = email === SUPER_ADMIN_EMAIL || SUPER_ADMIN_EMAIL === '';

        if (isAdmin) {
          setIsAuthenticated(true);
          setAdminUser(user);
          setIsSuperAdmin(isSuper);
        } else {
          // User is authenticated but not an admin - sign them out
          await signOut(auth);
          setIsAuthenticated(false);
          setAdminUser(null);
          setIsSuperAdmin(false);
        }
      } else {
        setIsAuthenticated(false);
        setAdminUser(null);
        setIsSuperAdmin(false);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Check if user is an admin
      const isAdmin = ADMIN_EMAILS.length === 0 || ADMIN_EMAILS.includes(user.email);
      if (!isAdmin) {
        await signOut(auth);
        return { success: false, error: 'Access denied. This account is not authorized.' };
      }

      return { success: true };
    } catch (error) {
      let errorMessage = 'Login failed. Please try again.';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email.';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later.';
          break;
        default:
          errorMessage = error.message || 'Login failed. Please try again.';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const loginWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;
      
      // Check if user is an admin
      const isAdmin = ADMIN_EMAILS.length === 0 || ADMIN_EMAILS.includes(user.email);
      if (!isAdmin) {
        await signOut(auth);
        return { success: false, error: 'Access denied. This account is not authorized.' };
      }

      return { success: true };
    } catch (error) {
      console.error('Firebase Google Sign-In Error:', error);
      console.error('Error Code:', error.code);
      console.error('Error Message:', error.message);
      
      let errorMessage = 'Google sign-in failed. Please try again.';
      
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign-in popup was closed.';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Popup was blocked. Please allow popups and try again.';
      } else if (error.code === 'auth/unauthorized-domain') {
        errorMessage = 'This domain is not authorized. Please contact the administrator.';
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'Google sign-in is not enabled. Please contact the administrator.';
      } else if (error.code === 'auth/invalid-api-key') {
        errorMessage = 'Invalid Firebase configuration. Please check your settings.';
      } else if (error.message) {
        errorMessage = `Google sign-in error: ${error.message}`;
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const value = {
    isAuthenticated,
    adminUsername: adminUser?.email || null,
    adminUser,
    isSuperAdmin,
    isLoading,
    login,
    loginWithGoogle,
    logout
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};
